import isUndefined from 'lodash/isUndefined';
import isObject from 'lodash/isObject';
import { pick } from 'lodash';
import { compareWithOperator, OPERATORS } from '@/utils';

export const CONDITION_NAMES = {
  HIDE: 'hide',
  HIDE_IF: 'hideIf',
  HIDE_IF_ANY: 'hideIfAny',
  HIDE_IF_ALL: 'hideIfAll',
  SHOW_IF: 'showIf',
  SHOW_IF_ANY: 'showIfAny',
  SHOW_IF_ALL: 'showIfAll',
  DISABLE_IF: 'disableIf',
  ENABLE_IF: 'enableIf',
  INCLUDE: 'include',
};


/**
 * Standarize the definition to allow backward compatibility.
 * @param params
 * @param paramName
 * @return {null|{field: *, value: *, operator: *}|*}
 */
const getVisibilityDefinition = (params, paramName) => {
  if (isUndefined(params[paramName])) {
    return null;
  }
  const definition = params[paramName];
  if (isObject(definition)) {
    return definition;
  }

  return {
    field: definition,
    value: true,
    operator: OPERATORS.EQUAL,
  };
};


function evalMixedConditions(mixedConditions, formValue) {
  const scalarConditions = mixedConditions.filter(item => typeof item !== 'object');
  const complexConditions = mixedConditions.filter(item => typeof item === 'object');

  // Complex check: use compare function with field, value, operator comparison objects
  const complexResults = complexConditions.map(({ field, value, operator }) => {
    if (isUndefined(field)
      || isUndefined(value)
      || isUndefined(operator)
    ) {
      console.error(
        `Wrong comparison configuration for field ${field}, value and operator expected`, { field, value, operator },
      );
      return false;
    }
    return compareWithOperator(formValue[field], value, operator);
  });
  // Simple check: look for values on formValue
  const scalarResults = Object.values(pick(formValue, scalarConditions));
  return [...complexResults, ...scalarResults];
}

export default {
  methods: {
    compareWithOperator,
    isVisible({ params = {} }) {
      const hide = params[CONDITION_NAMES.HIDE];
      if (hide === true) {
        return false;
      }
      if (params[CONDITION_NAMES.HIDE_IF_ANY]) {
        return !evalMixedConditions(params[CONDITION_NAMES.HIDE_IF_ANY], this.formValue).some(val => !!val);
      }

      if (params[CONDITION_NAMES.SHOW_IF_ANY]) {
        return evalMixedConditions(params[CONDITION_NAMES.SHOW_IF_ANY], this.formValue).some(val => !!val);
      }

      if (params[CONDITION_NAMES.HIDE_IF_ALL]) {
        return !evalMixedConditions(params[CONDITION_NAMES.HIDE_IF_ALL], this.formValue).every(val => !!val);
      }

      if (params[CONDITION_NAMES.SHOW_IF_ALL]) {
        return evalMixedConditions(params[CONDITION_NAMES.SHOW_IF_ALL], this.formValue).every(val => !!val);
      }

      const hideIfDefinition = getVisibilityDefinition(params, CONDITION_NAMES.HIDE_IF);
      if (hideIfDefinition !== null
        // eslint-disable-next-line max-len
        && compareWithOperator(this.formValue[hideIfDefinition.field], hideIfDefinition.value, hideIfDefinition.operator)) {
        return false;
      }

      const showIfDefinition = getVisibilityDefinition(params, CONDITION_NAMES.SHOW_IF);
      if (showIfDefinition !== null
        // eslint-disable-next-line max-len
        && !compareWithOperator(this.formValue[showIfDefinition.field], showIfDefinition.value, showIfDefinition.operator)) {
        return false;
      }

      return true;
    },
    isDisabled({ params = {} }) {
      if (params.disabled) {
        return params.disabled;
      }

      const disableIfDefinition = getVisibilityDefinition(params, CONDITION_NAMES.DISABLE_IF);
      if (disableIfDefinition !== null
        && compareWithOperator(
          this.formValue[disableIfDefinition.field],
          disableIfDefinition.value,
          disableIfDefinition.operator,
        )
      ) {
        return true;
      }

      const enableIfDefinition = getVisibilityDefinition(params, CONDITION_NAMES.ENABLE_IF);
      if (enableIfDefinition !== null
        // eslint-disable-next-line max-len
        && !compareWithOperator(this.formValue[enableIfDefinition.field], enableIfDefinition.value, enableIfDefinition.operator)) {
        return true;
      }

      return false;
    },
  },
};
